import { Button, Card, Select, Table, TableProps } from 'antd'
import clsx from 'clsx'

import ButtonDownload from 'component/button/button-download'
import LinkAddress from 'component/link/link-address'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { ALLOCATION_TITLE } from 'constants/vesting'
import { useVestingContextV2 } from 'context/vesting-context-provider-v2'
import { useVestingDataV2 } from 'hooks/vesting/use-vesting-data-v2'
import AllocationDropdownCell from 'modules/account-vesting-v2/allocation-dropdown-cell'
import ButtonCreate from 'modules/vesting-v2/button-create'
import ButtonImport from 'modules/vesting-v2/button-import'
import ButtonUpload from 'modules/vesting-v2/button-upload'
import {
  useCanManageWhitelist,
  useCanWriteWhitelist,
} from 'modules/vesting-v2/use-vesting-permission'

const ImportWhitelist = () => {
  const { contract, type: vestingType } = useVestingContextV2()
  const canManage = useCanManageWhitelist()
  const canWrite = useCanWriteWhitelist()

  const {
    loading,
    data,
    total,
    searchParams,
    getData,
    onLoadData,
    onChangeSearchParams,
    handleTableChange,
  } = useVestingDataV2({})

  const { page, limit, ownerAddress, status } = searchParams || {}

  const columnsWhitelist: TableProps<any>['columns'] = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => {
        return (
          (Number(page || DEFAULT_PAGE_INDEX) - 1) * Number(limit || DEFAULT_PAGE_SIZE) + index + 1
        )
      },
      width: 70,
      align: 'center',
    },
    {
      title: 'Address',
      dataIndex: 'ownerAddress',
      width: 400,
      key: 'ownerAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} max={0} />
        </div>
      ),
    },
    {
      title: vestingType ? ALLOCATION_TITLE[vestingType] : 'Allocation',
      width: 200,
      dataIndex: 'allocationWlV2',
      key: 'allocationWlV2',
      align: 'right',
      render: (items: { value: number; signDate: number }[] | undefined, record: any) => {
        const total = items?.reduce((sum, item) => sum + (item.value ?? 0), 0) ?? 0
        return <AllocationDropdownCell items={items ?? []} totalDisplay={total} />
      },
    },
    {
      title: 'Amount from File (MEY)',
      width: 200,
      dataIndex: 'allocationDetailsV2',
      key: 'allocationDetailsV2',

      render: (items: { value: number; signDate: number }[] | undefined, record: any) => {
        const total = items?.reduce((sum, item) => sum + (item.value ?? 0), 0) ?? 0
        return <AllocationDropdownCell items={items ?? []} totalDisplay={total} />
      },
    },
    {
      title: 'Amount from SmartContract (MEY) ',
      dataIndex: 'amountContractV2',
      width: 300,
      key: 'amountContractV2',
      render: (_, record: any) => {
        const items = (record?.amountContractV2 || []).map((item: any) => ({
          ...item,
          signDate: item.signDate,
        }))
        const total = items.reduce((sum: number, item: any) => sum + item.value, 0)
        return <AllocationDropdownCell items={items} totalDisplay={total} />
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      hidden: !canWrite,
      render: (text: any, record: any) => {
        return (
          <>
            <span
              className={clsx({
                'status-pending': !canWrite,
                'status-success': record.status,
                'status-fail': !record.status,
              })}
            >
              {!canWrite
                ? 'Need Import Whitelist'
                : record.status
                  ? 'Valid'
                  : 'Error - Needs to re-import'}
            </span>
          </>
        )
      },
    },
  ]

  return (
    <>
      {canManage && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'ce',
          }}
        >
          <div className="export-files">
            <ButtonUpload disable={!canWrite} onAfterUpload={onLoadData} />
            <ButtonDownload fileUrl="/example-v2.csv" />
          </div>
          <div className="export-files">
            <ButtonImport disable={!canWrite} onAfterImport={onLoadData} contract={contract} />
          </div>
        </div>
      )}
      <div className="flex-between">
        <div className="user-search">
          <input
            type="text"
            placeholder="Address"
            value={ownerAddress}
            onChange={(e) => {
              onChangeSearchParams({
                ownerAddress: e.target.value,
                page: DEFAULT_PAGE_INDEX,
              })
            }}
          />
          <Select
            allowClear
            placeholder="By status"
            options={[
              { value: null, label: 'All' },
              { value: false, label: 'Fail' },
              { value: true, label: 'Success' },
            ]}
            value={status}
            onChange={(value) => {
              onChangeSearchParams({
                status: value,
                page: DEFAULT_PAGE_INDEX,
              })
            }}
            style={{ width: '200px', textAlign: 'start' }}
          />
          <Button type="primary" onClick={() => getData(searchParams)}>
            Search
          </Button>
          <ButtonCreate onSubmit={onLoadData} />
        </div>
        <div className="user-action" />
      </div>
      <Card style={{ margin: '0 auto' }}>
        <Table<any>
          rowKey="_id"
          columns={columnsWhitelist}
          dataSource={data}
          loading={loading}
          pagination={
            total < 11
              ? false
              : {
                  total: total,
                  current: page,
                  pageSize: limit,
                }
          }
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          bordered
        />
      </Card>
    </>
  )
}

export default ImportWhitelist
